import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useArtieScore } from "@/hooks/use-artie-score";
import { apiClient } from "@/lib/api";
import {
  Loader2,
  ShieldAlert,
  CheckCircle2,
  XCircle,
  Search,
  FileText,
  Highlighter,
  MessageSquare,
  AlertTriangle,
  Check,
  X,
  ChevronRight,
  ChevronLeft,
  Download,
  MoreVertical,
  Shield
} from "lucide-react";
import logo from '../../assets/images/logo-artie.png';
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

export const BrandFitPage = () => {
    const { projectId } = useParams();
    const { scoreData, loading: scoreLoading } = useArtieScore(projectId);
    
    // Fetch Script linked to the project
    const { data: scripts, isLoading: scriptLoading } = useQuery({
        queryKey: ["scripts", projectId],
        queryFn: () => apiClient.getScriptsByProject(projectId!),
        enabled: !!projectId,
    });

    const { data: project } = useQuery({
        queryKey: ["project", projectId],
        queryFn: () => apiClient.getProject(projectId!),
        enabled: !!projectId,
    });

    const script = scripts?.[0];

    // Score Calculation
    // Brand Fit Score Logic
    // We calculate a numeric score representation from the status and details for display
    const brandFitStatus = scoreData?.brand_fit_status || 'red';
    const brandFitAnalysis = scoreData?.brand_fit_analysis;
    
    const getBrandFitStatusInfo = (status: string, analysis: any) => {
        let baseScore = 0;
        let label = "Pending";
        let color = "text-muted-foreground";
        let pill = "Processing";

        // 1. Determine Base Score from Traffic Light
        switch(status) {
            case 'green':
                baseScore = 85;
                label = "Strong Fit";
                color = "text-green-500";
                pill = "High Alignment";
                break;
            case 'yellow':
                baseScore = 65;
                label = "Moderate Fit";
                color = "text-yellow-500";
                pill = "Needs Adjustment";
                break;
            case 'red':
                baseScore = 45;
                label = "Limited Fit";
                color = "text-red-500";
                pill = "Low Alignment";
                break;
            default: return { label, color, score: 0, pill };
        }

        if (analysis) {
            // 2. Adjust based on Risk Level
            const riskLevel = analysis.brand_safety?.risk_level?.toLowerCase();
            if (riskLevel === 'high') baseScore -= 20;
            else if (riskLevel === 'medium') baseScore -= 10;
            
            // 3. Adjust based on Integration Potential
            const potential = analysis.integration_opportunities?.potential_level?.toLowerCase();
            if (potential === 'high') baseScore += 5;
            else if (potential === 'low') baseScore -= 5;

            // 4. Adjust based on Accessibility
            if (analysis.accessible_brand_pool === 0) baseScore -= 5;
        }

        // Clamp score 0-100
        const finalScore = Math.max(0, Math.min(100, baseScore));

        return { label, color, score: finalScore, pill };
    };
    
    const statusInfo = getBrandFitStatusInfo(brandFitStatus, brandFitAnalysis);

    // Configure inventory based on actual opportunities found
    const [preferences, setPreferences] = useState({
        titleSponsorship: false,
        midRoll: false,
        productPlacement: false,
    });

    useEffect(() => {
        if (scoreData?.brand_fit_analysis?.integration_opportunities) {
            const opps = scoreData.brand_fit_analysis.integration_opportunities;
            setPreferences({
                titleSponsorship: opps.visible_logo_opportunities > 0,
                midRoll: opps.product_mention_opportunities > 0,
                productPlacement: opps.product_use_opportunities > 0,
            });
        }
    }, [scoreData]);

    const [collapsedCategories, setCollapsedCategories] = useState<Record<string, boolean>>({});
    
    // Local state for interactive elements
    const [acceptedCategories, setAcceptedCategories] = useState<Set<string>>(new Set());
    const [declinedCategories, setDeclinedCategories] = useState<Set<string>>(new Set());
    const [acknowledgedRisks, setAcknowledgedRisks] = useState<Set<number>>(new Set());
    const [showOpportunitiesModal, setShowOpportunitiesModal] = useState(false);

    const toggleCategory = (category: string, action: 'approve' | 'decline') => {
        if (action === 'approve') {
            const newAccepted = new Set(acceptedCategories);
            const newDeclined = new Set(declinedCategories);
            if (newAccepted.has(category)) {
                newAccepted.delete(category);
            } else {
                newAccepted.add(category);
                newDeclined.delete(category);
            }
            setAcceptedCategories(newAccepted);
            setDeclinedCategories(newDeclined);
        } else {
            const newAccepted = new Set(acceptedCategories);
            const newDeclined = new Set(declinedCategories);
            if (newDeclined.has(category)) {
                newDeclined.delete(category);
            } else {
                newDeclined.add(category);
                newAccepted.delete(category);
            }
            setAcceptedCategories(newAccepted);
            setDeclinedCategories(newDeclined);
        }
    };

    const toggleRiskAcknowledgement = (idx: number) => {
        const newAcknowledged = new Set(acknowledgedRisks);
        if (newAcknowledged.has(idx)) {
            newAcknowledged.delete(idx);
        } else {
            newAcknowledged.add(idx);
        }
        setAcknowledgedRisks(newAcknowledged);
    };

    const handleExport = () => {
        if (!scoreData) return;
        
        const reportData = {
            project: project?.title,
            date: new Date().toISOString(),
            score: scoreData,
            decisions: {
                acceptedCategories: Array.from(acceptedCategories),
                declinedCategories: Array.from(declinedCategories),
                preferences: preferences
            }
        };

        const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `Artie_BrandFit_Report_${project?.title || "Project"}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    if (scoreLoading || scriptLoading) {
        return (
            <div className="flex justify-center py-20">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
        );
    }
    
    if (!scoreData) return <div className="text-center py-10">No data available</div>;

    const brandFit = scoreData.brand_fit_analysis;
    const categories = brandFit.category_mapping.primary_categories;
    const safetyConcerns = brandFit.brand_safety.concerns;

    // Craft Assessment for Script Quality (moved from Production Readiness)
    const readiness = scoreData.production_readiness_analysis;
    
    const getCraftColor = (rating: string) => {
        switch (rating?.toLowerCase()) {
            case 'strong': return "text-green-600";
            case 'on_track': return "text-blue-600";
            case 'needs_work': return "text-amber-600";
            default: return "text-muted-foreground";
        }
    };

    const getCraftBadgeColor = (rating: string) => {
        switch (rating?.toLowerCase()) {
            case 'strong': return "bg-green-100 text-green-700 border-green-200";
            case 'on_track': return "bg-blue-100 text-blue-700 border-blue-200";
            case 'needs_work': return "bg-amber-100 text-amber-700 border-amber-200";
            default: return "bg-secondary text-muted-foreground border-border";
        }
    };

    const formatCraftRating = (rating: string) => {
        switch (rating?.toLowerCase()) {
            case 'strong': return "Strong";
            case 'on_track': return "On Track";
            case 'needs_work': return "Needs Work";
            default: return "N/A";
        }
    };

    const craftMetrics = [
        {
            label: "Structure",
            rating: readiness?.craft_assessment?.structure || "N/A",
            color: getCraftColor(readiness?.craft_assessment?.structure),
            badgeColor: getCraftBadgeColor(readiness?.craft_assessment?.structure),
            displayRating: formatCraftRating(readiness?.craft_assessment?.structure || "N/A"),
        },
        {
            label: "Hook",
            rating: readiness?.craft_assessment?.hook || "N/A",
            color: getCraftColor(readiness?.craft_assessment?.hook),
            badgeColor: getCraftBadgeColor(readiness?.craft_assessment?.hook),
            displayRating: formatCraftRating(readiness?.craft_assessment?.hook || "N/A"),
        },
        {
            label: "Characters",
            rating: readiness?.craft_assessment?.characters || "N/A",
            color: getCraftColor(readiness?.craft_assessment?.characters),
            badgeColor: getCraftBadgeColor(readiness?.craft_assessment?.characters),
            displayRating: formatCraftRating(readiness?.craft_assessment?.characters || "N/A"),
        },
        {
            label: "Dialogue",
            rating: readiness?.craft_assessment?.dialogue || "N/A",
            color: getCraftColor(readiness?.craft_assessment?.dialogue),
            badgeColor: getCraftBadgeColor(readiness?.craft_assessment?.dialogue),
            displayRating: formatCraftRating(readiness?.craft_assessment?.dialogue || "N/A"),
        },
        {
            label: "Payoff",
            rating: readiness?.craft_assessment?.payoff || "N/A",
            color: getCraftColor(readiness?.craft_assessment?.payoff),
            badgeColor: getCraftBadgeColor(readiness?.craft_assessment?.payoff),
            displayRating: formatCraftRating(readiness?.craft_assessment?.payoff || "N/A"),
        },
    ];

    return (
        <div className="flex-1 flex flex-col items-center w-full min-w-0 relative h-[calc(100vh)] pt-16 md:pt-0">
             {/* Top Navigation */}
             <nav className="w-full px-4 sm:px-6 py-4 sm:py-6 flex items-center justify-between z-30">
                 <div className="flex items-center gap-2 flex-1 min-w-0 mr-2">
                     <Link to={`/projects/${projectId}`} className="md:hidden p-2 -ml-2 text-muted-foreground hover:text-foreground shrink-0">
                         <ChevronLeft className="w-5 h-5" />
                     </Link>
                     <div className="flex flex-col min-w-0">
                         <div className="flex items-center gap-2 mb-1">
                         <span className="text-xs font-ibm text-muted-foreground flex items-center gap-1 whitespace-nowrap overflow-hidden">
                             <Link to="/dashboard" className="hidden sm:inline hover:text-foreground cursor-pointer transition-colors">Dashboard</Link>
                             <ChevronRight className="hidden sm:block w-3 h-3 opacity-50" />
                             <Link to={`/projects/${projectId}`} className="hover:text-foreground cursor-pointer transition-colors truncate max-w-[100px] sm:max-w-[200px]">
                                 {project?.title || "The Last Barista"}
                             </Link>
                             <ChevronRight className="w-3 h-3 opacity-50 flex-shrink-0" />
                             <span className="text-foreground font-medium truncate">Brand Fit Analysis</span>
                         </span>
                     </div>
                     <h2 className="font-cabinet font-bold text-foreground text-lg sm:text-xl tracking-tight truncate">
                         Commercial Options Landscape
                     </h2>
                     </div>
                 </div>
                 
                 <div className="flex items-center gap-2 flex-shrink-0">
                         <button
                             onClick={handleExport}
                             className="px-3 py-1.5 bg-secondary text-foreground rounded-lg text-xs font-medium border border-border hover:bg-border transition-colors flex items-center gap-2"
                         >
                         <Download className="w-3.5 h-3.5" />
                         <span className="hidden sm:inline">Export Report</span>
                     </button>
                 </div>
             </nav>
 
             <div className="flex-1 flex flex-col lg:flex-row w-full gap-6 px-4 sm:px-6 pb-6 animate-in fade-in duration-500 overflow-hidden min-h-0">
                {/* LEFT PANEL: Decision Flow */}
                <div className="w-full lg:w-[420px] shrink-0 flex flex-col h-full border rounded-xl bg-card overflow-hidden">
                    
                    {/* Brand Fit Score Card */}
                    <div className="p-6 bg-foreground text-background shrink-0 relative overflow-hidden group">
                        <div className="absolute inset-0 opacity-10 bg-white/5"></div>
                        
                        <div className="relative z-10 w-full flex flex-col items-center justify-between gap-4">
                            <div className="flex justify-between w-full items-center">
                                <span className="text-xs font-medium opacity-70 font-ibm uppercase tracking-wider">Brand Fit Status</span>
                                <MoreVertical className="w-4 h-4 opacity-50" />
                            </div>

                            <div className="flex flex-col items-center gap-4 my-auto">
                                {/* Traffic Light Visual */}
                                <div className="w-16 p-2 bg-background/10 backdrop-blur-sm rounded-full flex flex-col items-center gap-2 border border-white/10">
                                    {/* Red (Off/On) */}
                                    {brandFitStatus === 'red' ? (
                                        <div className="w-12 h-12 rounded-full bg-red-500 shadow-[0_0_20px_rgba(239,68,68,0.5)] border border-red-400/50 flex items-center justify-center animate-pulse">
                                            <Check className="text-white opacity-90 w-6 h-6" />
                                        </div>
                                    ) : (
                                        <div className="w-12 h-12 rounded-full bg-red-500/20 border border-red-500/10"></div>
                                    )}

                                    {/* Yellow (Off/On) */}
                                    {brandFitStatus === 'yellow' ? (
                                        <div className="w-12 h-12 rounded-full bg-yellow-500 shadow-[0_0_20px_rgba(234,179,8,0.5)] border border-yellow-400/50 flex items-center justify-center animate-pulse">
                                            <Check className="text-white opacity-90 w-6 h-6" />
                                        </div>
                                    ) : (
                                        <div className="w-12 h-12 rounded-full bg-yellow-500/20 border border-yellow-500/10"></div>
                                    )}

                                    {/* Green (Off/On) */}
                                    {brandFitStatus === 'green' ? (
                                        <div className="w-12 h-12 rounded-full bg-green-500 shadow-[0_0_20px_rgba(34,197,94,0.5)] border border-green-400/50 flex items-center justify-center animate-pulse">
                                            <Check className="text-white opacity-90 w-6 h-6" />
                                        </div>
                                    ) : (
                                        <div className="w-12 h-12 rounded-full bg-green-500/20 border border-green-500/10"></div>
                                    )}
                                </div>

                                <h2 className={cn("text-2xl font-bold font-cabinet tracking-tight mt-2",
                                    brandFitStatus === 'green' ? 'text-green-400' :
                                    brandFitStatus === 'yellow' ? 'text-yellow-400' : 'text-red-400'
                                )}>
                                    {statusInfo.label}
                                </h2>
                            </div>

                            <div className="text-center">
                                <p className="text-xs opacity-70 font-ibm leading-relaxed max-w-[200px] mx-auto">
                                    {brandFitStatus === 'green' ? "High potential for brand partnerships." :
                                     brandFitStatus === 'yellow' ? "Good fit but some adjustments needed." :
                                     "Review content for better brand alignment."}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Scrollable Content Area */}
                    <div className="flex-1 overflow-y-auto p-6 space-y-6">
                        
                        {/* Craft Assessment Section */}
                        <div className="pb-6 border-b">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="font-cabinet font-semibold text-lg">Craft Calibration</h3>
                                <div className="flex items-center gap-2 text-[10px] text-muted-foreground bg-secondary px-3 py-1.5 rounded-full border border-border shadow-sm">
                                    <Shield className="w-3 h-3" />
                                    <span>Format-Indexed</span>
                                </div>
                            </div>
                            <p className="text-xs text-muted-foreground mb-4">
                                How well your creative is calibrated for {project?.format || 'your format'}. Not "good/bad" but "here's how this lands for your format."
                            </p>

                            <div className="space-y-3">
                                {craftMetrics.map((metric, idx) => (
                                    <div key={idx} className="p-4 bg-background border border-border rounded-xl hover:bg-secondary/20 transition-colors">
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm font-semibold text-foreground">{metric.label}</span>
                                            <span className={`text-xs font-semibold px-3 py-1 rounded-full border ${metric.badgeColor}`}>
                                                {metric.displayRating}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Decision Flow Header */}
                        <div className="pb-4 border-b">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="font-cabinet font-bold text-xl">Decision Flow</h3>
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200 font-medium">Auto-Saved</span>
                            </div>
                            <p className="text-xs text-muted-foreground mb-4">Configure inventory slots for sales team.</p>
                            
                            {/* Progress Bar */}
                            <div className="w-full h-1 bg-secondary rounded-full mb-1 overflow-hidden">
                                <div
                                    className="h-full bg-primary rounded-full transition-all duration-500"
                                    style={{ width: `${(Object.values(preferences).filter(Boolean).length / 3) * 100}%` }}
                                ></div>
                            </div>
                        </div>

                        {/* Decision Flow Content */}
                        <div className="space-y-8">
                        
                        {/* STEP 1: High Impact */}
                        <section className="animate-slide-up">
                            <div className="flex items-center gap-2 mb-2">
                                <div className="w-6 h-6 rounded bg-foreground text-background flex items-center justify-center text-xs font-bold font-cabinet">1</div>
                                <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">High Impact Inventory</h4>
                            </div>
                            
                            <div className="mb-6 flex items-start gap-4 p-4 bg-secondary/30 rounded-xl border border-border/50">
                                <div className="w-14 h-14 relative shrink-0 mr-12">
                                    <div className="absolute inset-0 bg-secondary rounded-full blur-lg opacity-50"></div>
                                    <img
                                        src={logo}
                                        alt="Artie"
                                        className="w-full h-full rounded-full object-cover border-2 border-background shadow-sm relative z-10"
                                    />
                                    <div className="absolute left-10 -top-1  bg-primary text-primary-foreground text-[10px] font-semibold px-2 py-0.5 rounded-lg shadow-sm z-20 border-2 border-background whitespace-nowrap">
                                        Artie Tip! 🐾
                                    </div>
                                </div>
                                <div className="flex-1 pt-1">
                                    <p className="text-xs text-muted-foreground leading-relaxed">
                                        <span className="font-bold text-foreground block mb-1">Unlock Premium Revenue</span>
                                        Select which premium formats you're open to. Enabling these signals to brands that you're ready for higher-value deals like title sponsorships.
                                    </p>
                                </div>
                            </div>

                            <div className="space-y-3">
                                {/* Toggle Item 1: Title Sponsorship (Logo) */}
                                <div className="flex items-center justify-between p-3 bg-background border rounded-xl">
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <p className="text-sm font-semibold text-foreground">Title Sponsorship</p>
                                            {brandFit.integration_opportunities.visible_logo_opportunities > 0 && (
                                                <span className="text-[10px] bg-secondary px-1.5 rounded text-muted-foreground">
                                                    {brandFit.integration_opportunities.visible_logo_opportunities} spots
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-[10px] text-muted-foreground">Main "Presented By" credit (Logos)</p>
                                    </div>
                                    <Switch
                                        checked={preferences.titleSponsorship}
                                        onCheckedChange={(c) => setPreferences(p => ({...p, titleSponsorship: c}))}
                                    />
                                </div>

                                {/* Toggle Item 2: Mid-Roll (Mentions) */}
                                <div className="flex items-center justify-between p-3 bg-background border rounded-xl">
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <p className="text-sm font-semibold text-foreground">Mid-Roll Ad Slot</p>
                                            {brandFit.integration_opportunities.product_mention_opportunities > 0 && (
                                                <span className="text-[10px] bg-secondary px-1.5 rounded text-muted-foreground">
                                                    {brandFit.integration_opportunities.product_mention_opportunities} spots
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-[10px] text-muted-foreground">60s integrated read (Mentions)</p>
                                    </div>
                                    <Switch
                                        checked={preferences.midRoll}
                                        onCheckedChange={(c) => setPreferences(p => ({...p, midRoll: c}))}
                                    />
                                </div>
                                
                                {/* Toggle Item 3: Product Placement (Use) */}
                                <div className="flex items-center justify-between p-3 bg-background border rounded-xl">
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <p className="text-sm font-semibold text-foreground">Product Placement</p>
                                            {brandFit.integration_opportunities.product_use_opportunities > 0 && (
                                                <span className="text-[10px] bg-secondary px-1.5 rounded text-muted-foreground">
                                                    {brandFit.integration_opportunities.product_use_opportunities} spots
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-[10px] text-muted-foreground">Integrate products into scenes (Use)</p>
                                    </div>
                                    <Switch
                                        checked={preferences.productPlacement}
                                        onCheckedChange={(c) => setPreferences(p => ({...p, productPlacement: c}))}
                                    />
                                </div>
                            </div>
                        </section>

                        {/* STEP 2: Category Integration */}
                        <section className="animate-slide-up delay-100">
                            <div className="flex items-center gap-2 mb-2">
                                <div className="w-6 h-6 rounded bg-secondary border text-foreground flex items-center justify-center text-xs font-bold font-cabinet">2</div>
                                <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Category Integrations</h4>
                            </div>

                            <div className="mb-6 flex items-start gap-4 p-4 bg-secondary/30 rounded-xl border border-border/50">
                                <div className="w-14 h-14 relative shrink-0 mr-12">
                                    <div className="absolute inset-0 bg-secondary rounded-full blur-lg opacity-50"></div>
                                    <img
                                        src={logo}
                                        alt="Artie"
                                        className="w-full h-full rounded-full object-cover border-2 border-background shadow-sm relative z-10"
                                    />
                                    <div className="absolute left-10 -top-1 bg-primary text-primary-foreground text-[10px] font-semibold px-2 py-0.5 rounded-lg shadow-sm z-20 border-2 border-background whitespace-nowrap">
                                        Match! 🐾
                                    </div>
                                </div>
                                <div className="flex-1 pt-1">
                                    <p className="text-xs text-muted-foreground leading-relaxed">
                                        <span className="font-bold text-foreground block mb-1">Target The Right Brands</span>
                                        Review the industries I've identified as natural matches. Approve the ones you'd like to work with to filter your deal flow effectively.
                                    </p>
                                </div>
                            </div>

                            <div className="space-y-4">
                                {categories.length > 0 ? (
                                    categories.map((category, idx) => {
                                        const isApproved = acceptedCategories.has(category);
                                        const isDeclined = declinedCategories.has(category);
                                        
                                        return (
                                            <div key={idx} className={cn(
                                                "p-4 border rounded-xl relative transition-all",
                                                isApproved ? "bg-green-50/50 border-green-200 ring-1 ring-green-500" :
                                                isDeclined ? "bg-background border-border opacity-50 grayscale" :
                                                "bg-background border-border hover:border-primary/50"
                                            )}>
                                                {isApproved && (
                                                    <div className="absolute -right-2 -top-2 bg-green-500 text-white p-1 rounded-full shadow-sm">
                                                        <Check className="w-3 h-3 stroke-[3]" />
                                                    </div>
                                                )}
                                                
                                                <div className="flex justify-between items-start mb-3">
                                                    <div>
                                                        <h5 className={cn(
                                                            "text-sm font-bold font-cabinet flex items-center gap-2",
                                                            isDeclined && "line-through text-muted-foreground"
                                                        )}>
                                                            {category}
                                                            <span className="text-[10px] font-normal text-muted-foreground bg-background px-1.5 py-0.5 rounded border no-underline">
                                                                {brandFit.integration_opportunities.category_alignment?.[category]?.length || 0} spots
                                                            </span>
                                                        </h5>
                                                        <p className="text-xs text-muted-foreground mt-1">Potential script matches detected</p>
                                                    </div>
                                                    {!isDeclined && !isApproved && <Search className="h-4 w-4 text-primary" />}
                                                </div>
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => toggleCategory(category, 'decline')}
                                                        className={cn(
                                                            "flex-1 py-1.5 border rounded text-xs font-medium transition-colors",
                                                            isDeclined
                                                                ? "bg-destructive text-destructive-foreground border-destructive"
                                                                : "bg-background text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                                                        )}
                                                    >
                                                        {isDeclined ? "Declined" : "Decline"}
                                                    </button>
                                                    <button
                                                        onClick={() => toggleCategory(category, 'approve')}
                                                        className={cn(
                                                            "flex-1 py-1.5 rounded text-xs font-medium transition-all",
                                                            isApproved
                                                                ? "bg-green-600 text-white shadow-sm"
                                                                : "bg-foreground text-background hover:opacity-90"
                                                        )}
                                                    >
                                                        {isApproved ? "Approved" : "Approve"}
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="p-4 bg-background border border-dashed rounded-xl text-center">
                                        <p className="text-xs text-muted-foreground">No specific categories identified automatically.</p>
                                    </div>
                                )}
                            </div>
                        </section>

                        {/* STEP 3: Brand Safety */}
                        <section className="animate-slide-up delay-200">
                            <div className="flex items-center gap-2 mb-2">
                                <div className="w-6 h-6 rounded bg-secondary border text-foreground flex items-center justify-center text-xs font-bold font-cabinet">3</div>
                                <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Safety Flags</h4>
                            </div>

                            <div className="mb-6 flex items-start gap-4 p-4 bg-secondary/30 rounded-xl border border-border/50">
                                <div className="w-14 h-14 relative shrink-0 mr-12">
                                    <div className="absolute inset-0 bg-secondary rounded-full blur-lg opacity-50"></div>
                                    <img
                                        src={logo}
                                        alt="Artie"
                                        className="w-full h-full rounded-full object-cover border-2 border-background shadow-sm relative z-10"
                                    />
                                    <div className="absolute left-10 -top-1 bg-primary text-primary-foreground text-[10px] font-semibold px-2 py-0.5 rounded-lg shadow-sm z-20 border-2 border-background whitespace-nowrap">
                                        Safe! 🐾
                                    </div>
                                </div>
                                <div className="flex-1 pt-1">
                                    <p className="text-xs text-muted-foreground leading-relaxed">
                                        <span className="font-bold text-foreground block mb-1">Build Trust with Transparency</span>
                                        Acknowledge these flags to show brands you're aware of the context. Proactive disclosure helps them assess safety faster.
                                    </p>
                                </div>
                            </div>

                            {safetyConcerns.length > 0 ? (
                                safetyConcerns.map((concern, idx) => {
                                    const isAcknowledged = acknowledgedRisks.has(idx);
                                    return (
                                        <div key={idx} className={cn(
                                            "p-4 rounded-xl mb-3 transition-all",
                                            isAcknowledged
                                                ? "bg-slate-50 border border-slate-200 opacity-60"
                                                : "bg-orange-50 border border-orange-100"
                                        )}>
                                            <div className="flex items-start gap-3">
                                                {isAcknowledged ? (
                                                    <CheckCircle2 className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                                                ) : (
                                                    <AlertTriangle className="h-4 w-4 text-orange-500 mt-0.5 shrink-0" />
                                                )}
                                                <div>
                                                    <h5 className={cn("text-sm font-bold", isAcknowledged ? "text-slate-600 line-through" : "text-orange-900")}>
                                                        Safety Flag Detected
                                                    </h5>
                                                    <p className={cn("text-xs mt-1 mb-3 leading-relaxed", isAcknowledged ? "text-slate-500" : "text-orange-800")}>
                                                        {concern}
                                                    </p>
                                                    
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            type="checkbox"
                                                            id={`safety-ack-${idx}`}
                                                            checked={isAcknowledged}
                                                            onChange={() => toggleRiskAcknowledgement(idx)}
                                                            className="w-4 h-4 rounded border-orange-300 text-orange-600 focus:ring-orange-500 bg-white cursor-pointer"
                                                        />
                                                        <label htmlFor={`safety-ack-${idx}`} className={cn("text-xs font-medium cursor-pointer", isAcknowledged ? "text-slate-500" : "text-orange-900")}>
                                                            {isAcknowledged ? "Risk Acknowledged" : "Acknowledge risk"}
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="p-4 bg-emerald-50 border border-emerald-100 rounded-xl flex items-center gap-3">
                                    <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                                    <span className="text-sm font-medium text-emerald-800">No major safety flags detected.</span>
                                </div>
                            )}
                        </section>

                        </div>
                    </div>
                </div>

                {/* RIGHT PANEL: Script Reference */}
                <div className="flex-1 bg-secondary rounded-xl border relative flex flex-col overflow-hidden">
                    {/* Script Toolbar */}
                    <div className="h-12 border-b bg-background flex items-center justify-between px-6 shrink-0 z-10">
                        <div className="flex items-center gap-4">
                            <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Script Preview</span>
                            <div className="h-4 w-[1px] bg-border"></div>
                            <div className="flex items-center gap-2 text-xs text-foreground">
                                <span className="w-2 h-2 rounded-full bg-primary"></span>
                                <span>Viewing: <span className="font-medium">{script?.filename || "Current Script"}</span></span>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-secondary text-muted-foreground">
                                <Highlighter className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-secondary text-muted-foreground">
                                <MessageSquare className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>

                    {/* Script Document Area */}
                    <div className="flex-1 overflow-y-auto bg-white flex justify-center">
                        <div className="w-full max-w-4xl p-8 lg:p-16 pb-[80vh] min-h-full relative">
                            
                            {/* Watermark */}
                            <div className="absolute top-12 right-12 opacity-5 font-cabinet font-black text-6xl lg:text-8xl text-foreground pointer-events-none -rotate-12 select-none z-0">
                                DRAFT
                            </div>

                            {/* Script Header */}
                            <div className="text-center mb-12 relative z-10">
                                <h1 className="font-ibm font-bold text-2xl uppercase underline decoration-2 underline-offset-4 mb-4">
                                    {scoreData.score_label || "Project Script"}
                                </h1>
                                <p className="font-ibm text-sm text-muted-foreground uppercase tracking-widest">
                                    {script ? new Date(script.created_at).toLocaleDateString() : "No Date"}
                                </p>
                            </div>

                            {/* Script Content */}
                            <div className="font-ibm text-sm leading-relaxed text-slate-800 space-y-6 font-medium font-mono whitespace-pre-wrap relative z-10">
                                {script?.extracted_text || (
                                    <div className="text-center text-muted-foreground italic mt-20">
                                        No script content found for this project.
                                        <br />
                                        Please upload a script to see the analysis.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Contextual Floating Action (Mock) */}
                    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 bg-foreground text-background px-4 py-3 rounded-xl shadow-xl flex items-center gap-4 animate-in slide-in-from-bottom-5 duration-500 z-30">
                         <div className="flex flex-col">
                            <span className="text-[10px] uppercase tracking-wide opacity-70">Action</span>
                            <span className="text-sm font-semibold">Highlight opportunities</span>
                         </div>
                         <div className="h-8 w-[1px] bg-white/20"></div>
                         <div className="flex gap-2">
                            <button
                                className="p-1 hover:bg-white/20 rounded transition-colors"
                                title="Show Opportunities"
                                onClick={() => setShowOpportunitiesModal(true)}
                            >
                                <Check className="h-4 w-4" />
                            </button>
                         </div>
                    </div>

                </div>
            </div>

            {/* Opportunities Modal */}
            <Dialog open={showOpportunitiesModal} onOpenChange={setShowOpportunitiesModal}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Natural Integration Opportunities</DialogTitle>
                        <DialogDescription>
                            AI-identified moments in the script where brand integrations would feel natural.
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 mt-4">
                        {brandFit.integration_opportunities.natural_integration_points.length > 0 ? (
                            brandFit.integration_opportunities.natural_integration_points.map((point, idx) => (
                                <div key={idx} className="p-4 border rounded-lg bg-secondary/10">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-xs font-bold uppercase tracking-wide text-primary bg-primary/10 px-2 py-1 rounded">
                                            {point.type}
                                        </span>
                                        <span className="text-xs text-muted-foreground font-mono">Scene: {point.scene}</span>
                                    </div>
                                    <p className="text-sm font-medium text-foreground">
                                        {point.opportunity}
                                    </p>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-8 text-muted-foreground">
                                No specific integration points identified yet.
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};